import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  formatEuro,
  RESERVE_METHOD_HINTS,
  RESERVE_METHOD_LABELS,
  RESERVE_METHODS,
  VVE_MIN_PERCENT,
  type MjopResponse,
  type ReserveMethod,
  type ReservePlanDto,
  type ReserveYear,
  type reserveByBuilding,
} from "@klantpunt/shared";
import { IconSettings } from "../../app/icons";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { parseEuro } from "../../pages/Platform";
import { shortEuro } from "./BudgetChart";
import { euroInput } from "./MjopFields";

type Reserve = ReturnType<typeof reserveByBuilding>;
type Fund = Reserve["buildings"][number];

/**
 * Reservering (docs/ASSETS-MJOP.md, "Reservering"): per gebouw het fonds met startsaldo, storting,
 * rente en de vervangingen (geen onderhoud: dat is operationeel); bij alle gebouwen een overzicht per gebouw met het totaal.
 */
export function ReservePanel({
  d,
  reserve,
  building,
  canEdit,
  onPickBuilding,
}: {
  d: MjopResponse;
  reserve: Reserve;
  building: string;
  canEdit: boolean;
  onPickBuilding: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const fund = building ? reserve.buildings.find((b) => b.buildingId === building) : undefined;
  const span = `${d.fromYear}–${d.reserveToYear}`;
  return (
    <section className="panel reserve" aria-label="Reservering">
      <div className="titlerow">
        <h2>Reservering</h2>
        {fund && canEdit && (
          <button type="button" className="btn small" onClick={() => setEditing(true)}>
            <IconSettings size={16} aria-hidden="true" />
            Fonds instellen
          </button>
        )}
      </div>
      {fund ? (
        <FundView fund={fund} span={span} fromYear={d.fromYear} />
      ) : (
        <FundsTable d={d} reserve={reserve} span={span} onPick={onPickBuilding} />
      )}
      {editing && fund && <PlanDialog plan={fund.plan} onClose={() => setEditing(false)} />}
    </section>
  );
}

function FundsTable({
  d,
  reserve,
  span,
  onPick,
}: {
  d: MjopResponse;
  reserve: Reserve;
  span: string;
  onPick: (id: string) => void;
}) {
  const name = new Map(d.buildings.map((b) => [b.id, `${b.code} · ${b.name}`]));
  const funds = reserve.buildings.filter((b) => b.years.some((y) => y.costsCents) || b.plan.saved);
  if (!funds.length) return <p className="muted">Nog niets begroot, dus nog niets te reserveren.</p>;
  return (
    <>
      <p className="muted small">
        Per gebouw een eigen fonds voor de vervangingen {span}; onderhoud is operationeel en telt niet mee. Kies een
        gebouw om het fonds in te stellen.
      </p>
      <div className="tscroll">
        <table className="it reserve-table names">
          <thead>
            <tr>
              <th>Gebouw</th>
              <th className="hide-mobile">Methode</th>
              <th className="num hide-mobile">Startsaldo</th>
              <th className="num">Storting per jaar</th>
              <th className="num hide-mobile">Laagste saldo</th>
            </tr>
          </thead>
          <tbody>
            {funds.map((b) => (
              <tr key={b.buildingId}>
                <th scope="row">
                  <button type="button" className="linkbtn inline" onClick={() => onPick(b.buildingId)}>
                    {name.get(b.buildingId) ?? "?"}
                  </button>
                </th>
                <td className="hide-mobile">
                  {b.plan.saved ? RESERVE_METHOD_LABELS[b.method] : <span className="muted">Standaard</span>}
                </td>
                <td className="num hide-mobile">{formatEuro(b.plan.startBalanceCents)}</td>
                <td className="num">{formatEuro(b.perYearCents)}</td>
                <td className="num hide-mobile">{formatEuro(b.lowestBalanceCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Totaal</th>
              <td className="hide-mobile" />
              <td className="num hide-mobile">{formatEuro(funds.reduce((s, b) => s + b.plan.startBalanceCents, 0))}</td>
              <td className="num">
                <strong>{formatEuro(reserve.perYearCents)}</strong>
              </td>
              <td className="hide-mobile" />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function FundView({ fund, span, fromYear }: { fund: Fund; span: string; fromYear: number }) {
  const p = fund.plan;
  const cur = fund.current;
  const vveOk = fund.vveMinimumCents == null ? null : (cur?.perYearCents ?? fund.perYearCents) >= fund.vveMinimumCents;
  return (
    <>
      <p className="muted small">
        {RESERVE_METHOD_LABELS[fund.method]} · vervangingen {span} · startsaldo {formatEuro(p.startBalanceCents)} op 1
        januari {p.balanceYear}
        {p.interestPercent ? ` · rente ${p.interestPercent.toLocaleString("nl-NL")}%` : ""}
      </p>
      {!p.saved && <p className="hint">Nog niet ingesteld: gerekend vanaf een leeg fonds, zonder rente.</p>}
      {p.saved && p.balanceYear < fromYear && (
        <p className="hint warn">
          Het startsaldo is van 1 januari {p.balanceYear}. Werk het bij voor {fromYear}.
        </p>
      )}
      <div className="pf-kpis" role="group" aria-label="Kerncijfers reservering">
        <span className="pf-kpi">
          <span className="label">Storting {fromYear}</span>
          <strong className="num" data-testid="reserve-contribution">
            {formatEuro(fund.perYearCents)}
          </strong>
          <span className="muted small">
            {fund.method === "cost_covering_indexed" ? "stijgt elk jaar met de index" : "per jaar"}
          </span>
        </span>
        <span className="pf-kpi">
          <span className="label">Laagste saldo</span>
          <strong className="num">{formatEuro(fund.lowestBalanceCents)}</strong>
          <span className="muted small">{fund.criticalYear != null ? `kritisch jaar ${fund.criticalYear}` : "—"}</span>
        </span>
        {cur && (
          <span className={`pf-kpi${cur.shortfallYear != null ? " bad" : ""}`}>
            <span className="label">Huidige storting</span>
            <strong className="num">{formatEuro(cur.perYearCents)}</strong>
            <span className="muted small" data-testid="reserve-current">
              {cur.shortfallYear != null
                ? `tekort in ${cur.shortfallYear}, laagste saldo ${formatEuro(cur.lowestBalanceCents)}`
                : "toereikend"}
            </span>
          </span>
        )}
        {fund.method === "components" && (
          <span className={`pf-kpi${fund.backlogCents ? " bad" : ""}`}>
            <span className="label">Hoort nu in het fonds</span>
            <strong className="num">{formatEuro(fund.requiredNowCents)}</strong>
            <span className="muted small">
              {fund.backlogCents ? `achterstand ${formatEuro(fund.backlogCents)}` : "geen achterstand"}
            </span>
          </span>
        )}
        {fund.vveMinimumCents != null && (
          <span className={`pf-kpi${vveOk ? "" : " bad"}`}>
            <span className="label">
              {VVE_MIN_PERCENT.toLocaleString("nl-NL")}% herbouw
              <wbr />
              waarde
            </span>
            <strong className="num">{formatEuro(fund.vveMinimumCents)}</strong>
            <span className="muted small">
              {vveOk ? "storting ligt erboven" : "storting ligt eronder"} (VvE zonder MJOP)
            </span>
          </span>
        )}
      </div>
      <SaldoChart years={fund.years} current={cur?.years ?? null} />
      <details className="reserve-details">
        <summary>Fonds per jaar</summary>
        <div className="tscroll">
          <table className="it reserve-table">
            <thead>
              <tr>
                <th>Jaar</th>
                <th className="num">Storting</th>
                <th className="num hide-mobile">Rente</th>
                <th className="num">Vervangingen</th>
                <th className="num">Saldo 31 dec</th>
              </tr>
            </thead>
            <tbody>
              {fund.years.map((y) => (
                <tr key={y.year} className={y.year === fund.criticalYear ? "crit" : undefined}>
                  <th scope="row">{y.year}</th>
                  <td className="num">{formatEuro(y.contributionCents)}</td>
                  <td className="num hide-mobile">{y.interestCents ? formatEuro(y.interestCents) : "—"}</td>
                  <td className="num">{y.costsCents ? formatEuro(y.costsCents) : "—"}</td>
                  <td className="num">{formatEuro(y.balanceCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      {fund.method === "components" && fund.components.length > 0 && (
        <details className="reserve-details" open>
          <summary>Per onderdeel</summary>
          <p className="muted small">
            Wat al versleten is, hoort nu klaar te liggen (bedrag × verstreken deel van de cyclus). De rest wordt tot en
            met de vervanging bijgelegd; daarna sparen voor de volgende keer.
          </p>
          <div className="tscroll">
            <table className="it reserve-table names">
              <thead>
                <tr>
                  <th>Onderdeel</th>
                  <th className="num hide-mobile">Vervanging</th>
                  <th className="num hide-mobile">Bedrag</th>
                  <th className="num hide-mobile">Versleten</th>
                  <th className="num hide-mobile">Nu nodig</th>
                  <th className="num hide-mobile">Toegerekend</th>
                  <th className="num">Per jaar</th>
                </tr>
              </thead>
              <tbody>
                {fund.components.map((c) => (
                  <tr key={c.sourceId}>
                    <th scope="row">{c.label}</th>
                    <td className="num hide-mobile">
                      {c.year} <span className="muted small">· {c.cycleYears} jr</span>
                    </td>
                    <td className="num hide-mobile">{formatEuro(c.costCents)}</td>
                    <td className="num hide-mobile">{Math.round(c.elapsed * 100)}%</td>
                    <td className="num hide-mobile">{formatEuro(c.requiredNowCents)}</td>
                    <td className="num hide-mobile">{formatEuro(c.allocatedCents)}</td>
                    <td className="num">{formatEuro(c.perYearCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </>
  );
}

/** Saldo op 31 december per jaar als staven; met een huidige storting ook die lijn (onder nul = tekort). */
function SaldoChart({ years, current }: { years: ReserveYear[]; current: ReserveYear[] | null }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(280, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = Math.round(Math.min(300, Math.max(200, W * 0.2)));
  const pad = { l: 64, r: 8, t: 8, b: 28 };
  const all = [...years, ...(current ?? [])].map((y) => y.balanceCents);
  const max = Math.max(1, ...all);
  const min = Math.min(0, ...all);
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const yOf = (v: number) => pad.t + ((max - v) / (max - min)) * ih;
  const slot = iw / Math.max(1, years.length);
  const bw = Math.min(28, slot * 0.6);
  const every = Math.max(1, Math.ceil(40 / slot));
  const line = current
    ?.map((y, k) => `${k ? "L" : "M"}${(pad.l + k * slot + slot / 2).toFixed(1)},${yOf(y.balanceCents).toFixed(1)}`)
    .join(" ");
  return (
    <figure className="bchart">
      <ul className="bchart-legend" aria-label="Legenda">
        <li>
          <i className="rchart-key" aria-hidden="true" />
          Saldo 31 december
        </li>
        {current && (
          <li>
            <i className="rchart-key cur" aria-hidden="true" />
            Met de huidige storting
          </li>
        )}
      </ul>
      <div className="bchart-wrap" ref={wrap}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Saldo van het fonds per jaar">
          {[max, min < 0 ? min : max / 2].map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={yOf(v)} y2={yOf(v)} className="bchart-grid" />
              <text x={pad.l - 8} y={yOf(v)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
                {v < 0 ? `− ${shortEuro(-v)}` : shortEuro(v)}
              </text>
            </g>
          ))}
          <line x1={pad.l} x2={W - pad.r} y1={yOf(0)} y2={yOf(0)} className="bchart-base" />
          <text x={pad.l - 8} y={yOf(0)} className="bchart-axis" textAnchor="end" dominantBaseline="middle">
            € 0
          </text>
          {years.map((y, k) => {
            const x = pad.l + k * slot + (slot - bw) / 2;
            const a = yOf(Math.max(0, y.balanceCents));
            const b = yOf(Math.min(0, y.balanceCents));
            return (
              <g key={y.year}>
                <rect x={x} y={a} width={bw} height={Math.max(0, b - a)} rx={2} className="rchart-bar">
                  <title>{`${y.year}: ${formatEuro(y.balanceCents)}`}</title>
                </rect>
                {k % every === 0 && (
                  <text x={x + bw / 2} y={H - 8} className="bchart-axis" textAnchor="middle">
                    {y.year}
                  </text>
                )}
              </g>
            );
          })}
          {line && <path d={line} className="rchart-cur" />}
          {current?.map((y, k) =>
            y.balanceCents < 0 ? (
              <circle
                key={y.year}
                cx={pad.l + k * slot + slot / 2}
                cy={yOf(y.balanceCents)}
                r={3}
                className="rchart-neg"
              >
                <title>{`${y.year}: tekort ${formatEuro(-y.balanceCents)}`}</title>
              </circle>
            ) : null,
          )}
        </svg>
      </div>
    </figure>
  );
}

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

function PlanDialog({ plan, onClose }: { plan: ReservePlanDto; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [method, setMethod] = useState<ReserveMethod>(plan.method);
  const [start, setStart] = useState(euroInput(plan.startBalanceCents));
  const [year, setYear] = useState(String(plan.balanceYear));
  const [current, setCurrent] = useState(euroInput(plan.currentContributionCents));
  const [interest, setInterest] = useState(String(plan.interestPercent).replace(".", ","));
  const [rebuild, setRebuild] = useState(euroInput(plan.rebuildValueCents));
  const [busy, setBusy] = useState(false);
  const optEuro = (v: string) => (v.trim() ? parseEuro(v) : null);
  const rate = Number(interest.trim().replace(",", ".") || "0");
  const bad =
    parseEuro(start || "0") === null ||
    !/^\d{4}$/.test(year.trim()) ||
    (current.trim() !== "" && parseEuro(current) === null) ||
    (rebuild.trim() !== "" && parseEuro(rebuild) === null) ||
    !Number.isFinite(rate) ||
    rate < 0 ||
    rate > 20;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (bad) return;
    setBusy(true);
    try {
      await api<ReservePlanDto>(`/buildings/${plan.buildingId}/reserve-plan`, {
        method: "PUT",
        body: {
          method,
          startBalanceCents: parseEuro(start || "0"),
          balanceYear: Number(year),
          currentContributionCents: optEuro(current),
          interestPercent: rate,
          rebuildValueCents: optEuro(rebuild),
        },
      });
      await qc.invalidateQueries({ queryKey: ["mjop"] });
      toast("Fonds opgeslagen");
      onClose();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Fonds instellen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="reserveform" className="btn primary" disabled={busy || bad}>
            {busy ? "Bezig met opslaan…" : "Opslaan"}
          </button>
        </>
      }
    >
      <form id="reserveform" className="stack" onSubmit={submit}>
        <label className="field">
          <span className="label">Methode</span>
          <select value={method} onChange={(e) => setMethod(e.target.value as ReserveMethod)}>
            {RESERVE_METHODS.map((m) => (
              <option key={m} value={m}>
                {RESERVE_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
          <span className="hint">{RESERVE_METHOD_HINTS[method]}</span>
        </label>
        <div className="formgrid">
          <label className="field">
            <span className="label">Saldo van het fonds (€)</span>
            <input inputMode="decimal" value={start} onChange={(e) => setStart(e.target.value)} placeholder="0,00" />
          </label>
          <label className="field">
            <span className="label">Op 1 januari</span>
            <input inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Huidige storting per jaar (€)</span>
            <input
              inputMode="decimal"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="leeg = onbekend"
            />
          </label>
          <label className="field">
            <span className="label">Rente over het saldo (%)</span>
            <input inputMode="decimal" value={interest} onChange={(e) => setInterest(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="label">Herbouwwaarde (€)</span>
          <input
            inputMode="decimal"
            value={rebuild}
            onChange={(e) => setRebuild(e.target.value)}
            placeholder="optioneel, voor de VvE-toets"
          />
          <span className="hint">
            Een VvE stort minimaal wat uit het MJOP volgt, of zonder MJOP {VVE_MIN_PERCENT.toLocaleString("nl-NL")}% van
            de herbouwwaarde (art. 5:126 BW).
          </span>
        </label>
      </form>
    </Modal>
  );
}
